import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import { HelmetProvider } from 'react-helmet-async'
import { ThemeProvider } from './context/ThemeContext'
import { NotificationProvider } from './context/NotificationContext'
import App from './App'
import './index.css'
import './styles/cinematic.css'
import './styles/print.css'
import AOS from 'aos'
import 'aos/dist/aos.css'

// ─── Chunk-load-failure recovery ────────────────────────────────────────────
// After a new deployment, a tab still running the old SPA lazily imports chunk
// hashes that the server has deleted (HTTP 404). Detect that failure and reload
// the page ONCE so the browser picks up the new index.html → new chunk hashes.
// Guards against reload loops with a sessionStorage flag.
if (typeof window !== 'undefined') {
  const CHUNK_ERROR_RE = /Failed to fetch dynamically imported module|Loading chunk \d+ failed|Loading CSS chunk|Importing a module script failed/

  function isChunkLoadError(reason) {
    if (!reason) return false
    if (reason instanceof Error && CHUNK_ERROR_RE.test(reason.message)) return true
    if (typeof reason === 'string' && CHUNK_ERROR_RE.test(reason)) return true
    return false
  }

  function handleChunkFailure() {
    try {
      if (sessionStorage.getItem('sma-chunk-reload') === '1') return
      sessionStorage.setItem('sma-chunk-reload', '1')
      window.location.reload()
    } catch (_) {
      // sessionStorage unavailable (private mode) — reload without the guard.
      window.location.reload()
    }
  }

  // Clear the guard once a new page load succeeds past the import phase.
  window.addEventListener('load', () => {
    try { sessionStorage.removeItem('sma-chunk-reload') } catch (_) {}
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault()
      handleChunkFailure()
    }
  })

  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error || event.message)) {
      event.preventDefault()
      handleChunkFailure()
    }
  })
}

// Initialize AOS (Animate On Scroll)
AOS.init({
  duration: 800,
  easing: 'ease-in-out',
  once: true,
  offset: 100,
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter>
            <NotificationProvider>
              <App />
            </NotificationProvider>
            <Toaster position="top-right" />
          </BrowserRouter>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </React.StrictMode>,
)
